import {
  LayoutDashboard,
  Users,
  GraduationCap,
  BookOpen,
  Calendar,
  ClipboardList,
  IndianRupee,
  Library,
  Bus,
  FileText,
  Bell,
  Clock,
  MessageSquare,
  Home,
  MoreHorizontal,
  ConciergeBell,
  CreditCard,
  School,
  Mail,
  Ticket,
} from 'lucide-react';
import {
  buildNavPathPermissions,
  getEffectivePermissions,
  hasFullSchoolNav,
  hasPermission,
  isSchoolObserver,
  FULL_SCHOOL_NAV_ROLES,
} from './featurePermissions';
import { needsSubscriptionGate } from './rolePaths';
import {
  isAnnouncementsViaWhatsapp,
  isSchoolFeatureEnabled,
  schoolFeatureForPath,
} from './schoolFeatures';
import {
  isModuleLocked,
  subscriptionModuleForPath,
} from './moduleAccess';
import {
  isCrmRole,
  canManageAssignedFeedback,
  canMarkCrmAttendance,
  canViewCrmReports,
  canViewCrmPlans,
  canAccessCrmTickets,
  canManageSchools,
  canManageCrmSalaries,
  canAccessCrmInquiries,
  canAccessCrmDashboard,
  canManageSubscriptionPlans,
} from './crmPermissions';

export { FULL_SCHOOL_NAV_ROLES, getEffectivePermissions, hasFullSchoolNav, hasPermission };

/** Roles that use staff nav groups (filtered by effective_permissions). */
export const SCHOOL_STAFF_ROLES = ['school_admin', 'principal', 'teacher', 'staff', 'receptionist'];

/** Portal roles — flat permission-based nav only. */
export const PORTAL_ROLES = ['student', 'parent'];

/** Path → permissions required (any one grants access). Derived from FEATURE_CAPS. */
const _navBase = buildNavPathPermissions();
if (_navBase['/fees/management']) {
  _navBase['/fees'] = _navBase['/fees/management'];
  _navBase['/fees/accounts'] = _navBase['/fees/management'];
  _navBase['/fees/accounts/dues'] = _navBase['/fees/management'];
}
const _reportsPerms = _navBase['/reports/report-card'];
if (_reportsPerms) {
  _navBase['/reports/transfer-certificate'] = _reportsPerms;
  _navBase['/reports/certificate'] = _reportsPerms;
  _navBase['/reports/id-card'] = _reportsPerms;
  _navBase['/reports/attendance'] = _reportsPerms;
  _navBase['/reports/whatsapp-logs'] = _reportsPerms;
}
_navBase['/whatsapp/broadcast'] = _navBase['/announcements'] || ['manage_academics'];

/** Portal-specific paths */
_navBase['/portal/student/academics'] = ['view_own_data'];
_navBase['/portal/student/timetable'] = ['view_own_data'];
_navBase['/portal/student/homework'] = ['view_assignments'];
_navBase['/portal/student/exams'] = ['view_results'];
_navBase['/portal/student/marks'] = ['view_results'];
_navBase['/portal/student/report-card'] = ['view_own_data', 'view_results'];
_navBase['/leave'] = ['approve_leave', 'manage_leave'];
const _receptionPerms = _navBase['/reception'];
if (_receptionPerms) {
  _navBase['/reception/inquiries'] = _receptionPerms;
  _navBase['/reception/visitors'] = _receptionPerms;
  _navBase['/reception/phone-calls'] = _receptionPerms;
  _navBase['/reception/complaints'] = _receptionPerms;
  _navBase['/reception/appointments'] = _receptionPerms;
  _navBase['/reception/followups'] = _receptionPerms;
  _navBase['/reception/reports'] = ['view_reception_reports', ..._receptionPerms];
}
_navBase['/portal/student/attendance'] = ['view_attendance', 'view_own_data'];
_navBase['/portal/student/leave'] = ['apply_leave'];
_navBase['/portal/student/fees'] = ['view_own_data'];
_navBase['/portal/student/profile'] = ['view_own_data'];
_navBase['/portal/parent/children'] = ['view_child_data'];
_navBase['/portal/parent/attendance'] = ['view_attendance', 'view_child_data'];
_navBase['/portal/parent/leave'] = ['apply_leave', 'view_child_data'];
_navBase['/portal/parent/homework'] = ['view_assignments', 'view_child_data'];
_navBase['/portal/parent/timetable'] = ['view_child_data'];
_navBase['/portal/parent/exams'] = ['view_results', 'view_child_data'];
_navBase['/portal/parent/fees'] = ['view_fees', 'view_child_data'];
_navBase['/portal/parent/messages'] = ['view_portal_messages'];

export const NAV_PATH_PERMISSIONS = _navBase;

export const ANNOUNCEMENTS_NAV_ITEM = { path: '/announcements', icon: Bell, label: 'Announcements' };

const ANNOUNCEMENTS_PATH = '/announcements';

export const NAV_GROUPS = [
  {
    key: 'academics',
    label: 'Academics',
    icon: BookOpen,
    items: [
      { path: '/subjects', icon: BookOpen, label: 'Subjects' },
      { path: '/classes', icon: BookOpen, label: 'Classes' },
      { path: '/timetable', icon: Clock, label: 'Timetable' },
      { path: '/exams', icon: FileText, label: 'Exams' },
      { path: '/attendance', icon: ClipboardList, label: 'Attendance' },
      { path: '/leave', icon: ClipboardList, label: 'Leave Applications' },
      { path: '/events', icon: Calendar, label: 'Calendar' },
    ],
  },
  {
    key: 'communication',
    label: 'Communication',
    icon: MessageSquare,
    items: [
      { path: '/whatsapp/broadcast', icon: MessageSquare, label: 'WhatsApp Broadcast' },
    ],
  },
  {
    key: 'admission',
    label: 'Admission',
    icon: GraduationCap,
    items: [{ path: '/students', icon: GraduationCap, label: 'Students' }],
  },
  {
    key: 'reception',
    label: 'Reception',
    icon: ConciergeBell,
    items: [
      { path: '/reception', icon: ConciergeBell, label: 'Dashboard' },
      { path: '/reception/inquiries', icon: GraduationCap, label: 'Admission Inquiries' },
      { path: '/reception/visitors', icon: Users, label: 'Visitors' },
      { path: '/reception/phone-calls', icon: MessageSquare, label: 'Phone Calls' },
      { path: '/reception/complaints', icon: ClipboardList, label: 'Complaints' },
      { path: '/reception/appointments', icon: Calendar, label: 'Appointments' },
      { path: '/reception/followups', icon: Clock, label: 'Follow-ups' },
      { path: '/reception/reports', icon: FileText, label: 'Reports' },
    ],
  },
  {
    key: 'study',
    label: 'Study Material',
    icon: Library,
    items: [
      { path: '/library', icon: Library, label: 'Library' },
      { path: '/assignments', icon: BookOpen, label: 'Assignments' },
    ],
  },
  {
    key: 'transport',
    label: 'Transport',
    icon: Bus,
    items: [{ path: '/transport', icon: Bus, label: 'Transport' }],
  },
  {
    key: 'hr',
    label: 'HR',
    icon: Users,
    items: [
      { path: '/hr/teachers', icon: Users, label: 'Teachers' },
      { path: '/teachers/attendance', icon: ClipboardList, label: 'Teacher Attendance' },
    ],
  },
  {
    key: 'fees',
    label: 'Fees',
    icon: IndianRupee,
    items: [
      { path: '/fees/management', icon: IndianRupee, label: 'Fee Management' },
      { path: '/fees/accounts', icon: IndianRupee, label: 'Accounts' },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    icon: FileText,
    items: [
      { path: '/reports/report-card', icon: FileText, label: 'Report Card' },
      { path: '/reports/attendance', icon: FileText, label: 'Attendance Report' },
      { path: '/reports/transfer-certificate', icon: FileText, label: 'Transfer Certificate' },
      { path: '/reports/certificate', icon: FileText, label: 'Certificate' },
      { path: '/reports/id-card', icon: FileText, label: 'ID Card' },
      { path: '/reports/whatsapp-logs', icon: MessageSquare, label: 'WhatsApp Logs' },
    ],
  },
];

function isSchoolFeatureNavItemEnabled(user, item) {
  const moduleKey = schoolFeatureForPath(item.path);
  if (moduleKey && !isSchoolFeatureEnabled(user, moduleKey)) {
    return false;
  }
  return true;
}

function stripAnnouncementsFromGroups(groups) {
  return groups.map((group) => ({
    ...group,
    items: (group.items || []).filter((item) => item.path !== ANNOUNCEMENTS_PATH),
  }));
}

function canShowAnnouncementsNav(user) {
  if (!user) return false;
  if (hasFullSchoolNav(user)) return true;
  return canAccessNavPath(user, ANNOUNCEMENTS_PATH);
}

function applyAnnouncementsPlacement(user, groups) {
  if (!canShowAnnouncementsNav(user)) {
    return stripAnnouncementsFromGroups(groups).filter((group) => group.items.length > 0);
  }

  const targetKey = isAnnouncementsViaWhatsapp(user) ? 'communication' : 'academics';
  const stripped = stripAnnouncementsFromGroups(groups);

  return stripped
    .map((group) => {
      if (group.key !== targetKey) return group;
      const items = [...(group.items || [])];
      if (!items.some((item) => item.path === ANNOUNCEMENTS_PATH)) {
        items.push({ ...ANNOUNCEMENTS_NAV_ITEM });
      }
      return { ...group, items };
    })
    .filter((group) => group.items.length > 0);
}

export const STUDENT_PORTAL_NAV_ITEMS = [
  { path: '/portal/student/academics', icon: GraduationCap, label: 'My Class', anyPermissions: NAV_PATH_PERMISSIONS['/portal/student/academics'] },
  { path: '/portal/student/timetable', icon: Clock, label: 'Timetable', anyPermissions: NAV_PATH_PERMISSIONS['/portal/student/timetable'] },
  { path: '/portal/student/homework', icon: BookOpen, label: 'Homework', anyPermissions: NAV_PATH_PERMISSIONS['/portal/student/homework'] },
  { path: '/portal/student/exams', icon: FileText, label: 'Exams', anyPermissions: NAV_PATH_PERMISSIONS['/portal/student/exams'] },
  { path: '/portal/student/marks', icon: FileText, label: 'Marks', anyPermissions: NAV_PATH_PERMISSIONS['/portal/student/marks'] },
  { path: '/portal/student/report-card', icon: FileText, label: 'Report Card', anyPermissions: NAV_PATH_PERMISSIONS['/portal/student/report-card'] },
  { path: '/portal/student/attendance', icon: ClipboardList, label: 'Attendance', anyPermissions: NAV_PATH_PERMISSIONS['/portal/student/attendance'] },
  { path: '/portal/student/leave', icon: ClipboardList, label: 'Leave Applications', anyPermissions: NAV_PATH_PERMISSIONS['/portal/student/leave'] },
  { path: '/portal/student/fees', icon: IndianRupee, label: 'Fees', anyPermissions: NAV_PATH_PERMISSIONS['/portal/student/fees'] },
  { path: '/profile', icon: Users, label: 'Profile', anyPermissions: null },
];

export const PARENT_PORTAL_NAV_ITEMS = [
  { path: '/portal/parent/children', icon: GraduationCap, label: 'Children', anyPermissions: NAV_PATH_PERMISSIONS['/portal/parent/children'] },
  { path: '/portal/parent/attendance', icon: ClipboardList, label: 'Attendance', anyPermissions: NAV_PATH_PERMISSIONS['/portal/parent/attendance'] },
  { path: '/portal/parent/leave', icon: ClipboardList, label: 'Leave Applications', anyPermissions: NAV_PATH_PERMISSIONS['/portal/parent/leave'] },
  { path: '/portal/parent/homework', icon: BookOpen, label: 'Homework', anyPermissions: NAV_PATH_PERMISSIONS['/portal/parent/homework'] },
  { path: '/portal/parent/timetable', icon: Clock, label: 'Timetable', anyPermissions: NAV_PATH_PERMISSIONS['/portal/parent/timetable'] },
  { path: '/portal/parent/exams', icon: FileText, label: 'Exams', anyPermissions: NAV_PATH_PERMISSIONS['/portal/parent/exams'] },
  { path: '/portal/parent/fees', icon: IndianRupee, label: 'Fees', anyPermissions: NAV_PATH_PERMISSIONS['/portal/parent/fees'] },
  { path: '/portal/parent/messages', icon: MessageSquare, label: 'Messages', anyPermissions: NAV_PATH_PERMISSIONS['/portal/parent/messages'] },
  { path: '/profile', icon: Users, label: 'Profile', anyPermissions: null },
];

/** @deprecated use getPortalNavItems */
export const PORTAL_NAV_ITEMS = STUDENT_PORTAL_NAV_ITEMS;

export function getPortalNavItems(user) {
  if (user?.role_slug === 'parent') {
    return PARENT_PORTAL_NAV_ITEMS;
  }
  if (user?.role_slug === 'student') {
    return STUDENT_PORTAL_NAV_ITEMS;
  }
  return PORTAL_NAV_ITEMS;
}

export function isNavPathLocked(user, path) {
  return isModuleLocked(user, path);
}

export function canNavigateToPath(user, path) {
  if (!user || !path) return false;
  if (path === '/dashboard' || path === '/profile') return true;
  if (isModuleLocked(user, path)) return false;

  const moduleKey = schoolFeatureForPath(path);
  if (moduleKey && !isSchoolFeatureEnabled(user, moduleKey)) {
    return false;
  }
  if (hasFullSchoolNav(user)) return true;

  const perms = NAV_PATH_PERMISSIONS[path];
  if (!perms || perms.length === 0) return false;

  const effective = getEffectivePermissions(user);
  return perms.some((p) => effective.includes(p));
}

export function canAccessNavPath(user, path) {
  if (!user || !path) return false;
  if (path === '/dashboard' || path === '/profile') return true;

  if (user.role_slug === 'school_admin' && isModuleLocked(user, path)) {
    return true;
  }

  const moduleKey = schoolFeatureForPath(path);
  if (moduleKey && !isSchoolFeatureEnabled(user, moduleKey)) {
    return false;
  }
  if (hasFullSchoolNav(user)) return true;

  const perms = NAV_PATH_PERMISSIONS[path];
  if (!perms || perms.length === 0) return false;

  const effective = getEffectivePermissions(user);
  return perms.some((p) => effective.includes(p));
}

export function filterNavItem(user, item) {
  if (item.anyPermissions === null) return true;

  const moduleKey = schoolFeatureForPath(item.path);
  if (moduleKey && !isSchoolFeatureEnabled(user, moduleKey)) {
    return false;
  }
  if (item.anyPermissions) {
    if (hasFullSchoolNav(user)) return true;
    const effective = getEffectivePermissions(user);
    return item.anyPermissions.some((p) => effective.includes(p));
  }
  return canAccessNavPath(user, item.path);
}

export function filterNavGroups(user, navGroups = NAV_GROUPS) {
  return navGroups
    .map((group) => ({
      ...group,
      items: (group.items || []).filter((item) => filterNavItem(user, item)),
    }))
    .filter((group) => group.items.length > 0);
}

function annotateNavItemWithLockState(user, item) {
  const moduleKey = subscriptionModuleForPath(item.path);
  const locked = user?.role_slug === 'school_admin' && isModuleLocked(user, item.path);
  return { ...item, moduleKey, locked: Boolean(locked) };
}

/**
 * Staff sidebar groups: hide sections with zero accessible items; within visible
 * sections show only granted modules. School admin keeps full nav with lock state.
 */
export function resolveNavGroups(user, navGroups = NAV_GROUPS) {
  let groups;
  if (hasFullSchoolNav(user) && user?.role_slug === 'school_admin') {
    groups = navGroups
      .map((group) => ({
        ...group,
        items: (group.items || []).map((item) => annotateNavItemWithLockState(user, item)),
      }))
      .filter((group) => group.items.length > 0);
  } else if (hasFullSchoolNav(user)) {
    groups = navGroups
      .map((group) => ({
        ...group,
        items: (group.items || []).filter((item) => isSchoolFeatureNavItemEnabled(user, item)),
      }))
      .filter((group) => group.items.length > 0);
  } else {
    groups = navGroups
      .map((group) => {
        const items = (group.items || [])
          .filter((item) => isSchoolFeatureNavItemEnabled(user, item))
          .filter((item) => canAccessNavPath(user, item.path));

        if (items.length === 0) return null;
        return { ...group, items };
      })
      .filter(Boolean);
  }

  groups = applyAnnouncementsPlacement(user, groups);

  if (user?.role_slug === 'receptionist') {
    groups = groups
      .map((group) => {
        if (group.key !== 'reception') return group;
        return {
          ...group,
          items: (group.items || []).filter((item) => item.path !== '/reception'),
        };
      })
      .filter((group) => group.items.length > 0);
  }

  return groups;
}

export function filterPortalNavItems(user, items) {
  const list = items ?? getPortalNavItems(user);
  return list.filter((item) => filterNavItem(user, item));
}

export function canAccessSettings(user) {
  if (!user) return false;
  if (isSchoolObserver(user)) return false;
  const role = user.role_slug;
  if (['super_admin', 'school_admin'].includes(role)) {
    return true;
  }
  if (hasPermission(user, 'manage_platform_settings') || hasPermission(user, 'manage_platform_roles')) {
    return true;
  }
  return hasPermission(user, 'manage_school') || hasPermission(user, 'manage_users');
}

export function canEditRolePermissions(user) {
  if (!user) return false;
  if (isSchoolObserver(user)) return false;
  if (user.role_slug === 'super_admin' || user.role_slug === 'school_admin') return true;
  return hasPermission(user, 'manage_users');
}

/** Mobile bottom tabs derived from nav access. */
export const MOBILE_TAB_DEFS = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', always: true },
  { key: 'attendance', label: 'Attendance', icon: ClipboardList, path: '/attendance' },
  { key: 'students', label: 'Students', icon: Users, path: '/students' },
  { key: 'timetable', label: 'Timetable', icon: Clock, path: '/timetable' },
  { key: 'more', label: 'More', icon: MoreHorizontal, action: 'openMenu', always: true },
];

export const SCHOOL_ADMIN_MOBILE_TABS = [
  { key: 'dashboard', label: 'Home', icon: LayoutDashboard, path: '/dashboard', always: true },
  { key: 'teacher_attendance', label: 'Attend.', icon: ClipboardList, path: '/teachers/attendance', ariaLabel: 'Teachers Attendance' },
  { key: 'teachers', label: 'Teachers', icon: Users, path: '/hr/teachers' },
  { key: 'timetable', label: 'Timetable', icon: Clock, path: '/timetable' },
  { key: 'more', label: 'More', icon: MoreHorizontal, action: 'openMenu', always: true },
];

export const STUDENT_MOBILE_TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', always: true },
  { key: 'attendance', label: 'Attendance', icon: ClipboardList, path: '/portal/student/attendance' },
  { key: 'timetable', label: 'Schedule', icon: Clock, path: '/portal/student/timetable' },
  { key: 'more', label: 'More', icon: MoreHorizontal, action: 'openMenu', always: true },
];

export const PARENT_MOBILE_TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: Home, path: '/dashboard', always: true },
  { key: 'children', label: 'Children', icon: GraduationCap, path: '/portal/parent/children' },
  { key: 'attendance', label: 'Attendance', icon: ClipboardList, path: '/portal/parent/attendance' },
  { key: 'more', label: 'More', icon: MoreHorizontal, action: 'openMenu', always: true },
];

export const RECEPTIONIST_MOBILE_TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/reception', always: true },
  { key: 'inquiries', label: 'Inquiries', icon: GraduationCap, path: '/reception/inquiries' },
  { key: 'followups', label: 'Follow-ups', icon: Clock, path: '/reception/followups' },
  { key: 'more', label: 'More', icon: MoreHorizontal, action: 'openMenu', always: true },
];

/** Shown until school admin completes first subscription payment. */
export const SUBSCRIPTION_GATE_MOBILE_TABS = [
  { key: 'upgrade', label: 'Choose plan', icon: CreditCard, path: '/settings/subscription/upgrade', always: true },
  { key: 'subscription', label: 'Billing', icon: IndianRupee, path: '/settings/subscription', always: true },
  { key: 'more', label: 'More', icon: MoreHorizontal, action: 'openMenu', always: true },
];

const CRM_MORE_TAB = { key: 'more', label: 'More', icon: MoreHorizontal, action: 'openMenu', always: true };

export const CRM_SUPER_ADMIN_TABS = [
  { key: 'dashboard', label: 'CRM', icon: LayoutDashboard, path: '/crm/dashboard', always: true },
  { key: 'enquiries', label: 'Inquiries', icon: Mail, path: '/crm/inquiries', always: true },
  { key: 'schools', label: 'Schools', icon: School, path: '/schools', always: true },
  { key: 'salaries', label: 'Payroll', icon: IndianRupee, path: '/crm/salaries', always: true },
  { key: 'tickets', label: 'Tickets', icon: Ticket, path: '/crm/tickets', always: true },
  CRM_MORE_TAB,
];

/** Permission-aware CRM bottom tabs (up to 5 primary + More). */
export function getCrmMobileTabs(user) {
  if (!user || !isCrmRole(user) || isSchoolObserver(user)) {
    return null;
  }

  const tabs = [];

  if (canAccessCrmDashboard(user) || user.role_slug === 'super_admin') {
    tabs.push({ key: 'dashboard', label: canManageSchools(user) ? 'CRM' : 'Home', icon: LayoutDashboard, path: '/crm/dashboard', always: true });
  }
  if (canAccessCrmInquiries(user)) {
    tabs.push({ key: 'enquiries', label: 'Inquiries', icon: Mail, path: '/crm/inquiries', always: true });
  }
  if (canManageSchools(user)) {
    tabs.push({ key: 'schools', label: 'Schools', icon: School, path: '/schools', always: true });
  }
  if (canManageCrmSalaries(user) || canManageSchools(user)) {
    tabs.push({ key: 'salaries', label: 'Payroll', icon: IndianRupee, path: '/crm/salaries', always: true });
  }
  if (canAccessCrmTickets(user)) {
    tabs.push({ key: 'tickets', label: 'Tickets', icon: Ticket, path: '/crm/tickets' });
  }
  if (canViewCrmReports(user) && !canManageSchools(user)) {
    tabs.push({ key: 'reports', label: 'Reports', icon: FileText, path: '/crm/reports' });
  }
  if (canMarkCrmAttendance(user) && !canManageSchools(user)) {
    tabs.push({
      key: 'attendance',
      label: 'Attend.',
      icon: ClipboardList,
      path: '/crm/attendance',
      ariaLabel: 'Attendance',
    });
  }
  if (canManageAssignedFeedback(user) && !canManageSchools(user)) {
    tabs.push({ key: 'feedback', label: 'Feedback', icon: MessageSquare, path: '/crm/feedback' });
  }
  if (canViewCrmPlans(user) && !canManageSubscriptionPlans(user) && !canManageSchools(user)) {
    tabs.push({ key: 'plans', label: 'Plans', icon: CreditCard, path: '/crm/plans' });
  }

  // Dedupe by key, keep first 5, always append More
  const seen = new Set();
  const primary = [];
  for (const tab of tabs) {
    if (seen.has(tab.key)) continue;
    seen.add(tab.key);
    primary.push(tab);
    if (primary.length >= 5) break;
  }
  primary.push(CRM_MORE_TAB);
  return primary;
}

export function getVisibleMobileTabs(user) {
  if (needsSubscriptionGate(user)) {
    return SUBSCRIPTION_GATE_MOBILE_TABS;
  }
  const crmTabs = getCrmMobileTabs(user);
  if (crmTabs) {
    return crmTabs;
  }
  if (user?.role_slug === 'receptionist') {
    return RECEPTIONIST_MOBILE_TABS.filter((tab) => tab.always || canAccessNavPath(user, tab.path));
  }
  if (user?.role_slug === 'student') {
    return STUDENT_MOBILE_TABS.filter((tab) => tab.always || canAccessNavPath(user, tab.path));
  }
  if (user?.role_slug === 'parent') {
    return PARENT_MOBILE_TABS.filter((tab) => tab.always || canAccessNavPath(user, tab.path));
  }
  if (user?.role_slug === 'school_admin') {
    return SCHOOL_ADMIN_MOBILE_TABS.filter((tab) => tab.always || canAccessNavPath(user, tab.path));
  }
  return MOBILE_TAB_DEFS.filter((tab) => {
    if (tab.always) return true;
    return canAccessNavPath(user, tab.path);
  });
}
